import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, ChevronDown, ChevronRight, FileCode2,
  Route, FlaskConical, Clock, ArrowLeft, Copy, Check, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminRouterMap
 * --------------
 * Reads the single-source-of-truth `services.router_registry.EXTRACTED_ROUTERS`
 * list (via /api/admin/router-map) and renders every router with its live
 * endpoint count, file stats, matching contract tests, and collapsible
 * endpoint detail. Purpose: a new dev can learn the full app surface in
 * 5 minutes instead of 2 hours.
 *
 * Admin-only (gated server-side via require_role(['admin'])).
 */
const AdminRouterMap = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  const [copied, setCopied] = useState(null);
  const [sortBy, setSortBy] = useState('endpoints'); // endpoints | lines | name | modified

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/admin/router-map`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    let rows = data.routers.filter((r) => {
      if (!q) return true;
      if (r.short_name.toLowerCase().includes(q)) return true;
      if (r.note.toLowerCase().includes(q)) return true;
      if (r.module_path.toLowerCase().includes(q)) return true;
      if (r.endpoints.some((e) => e.path.toLowerCase().includes(q))) return true;
      return false;
    });
    const sorters = {
      endpoints: (a, b) => b.endpoint_count - a.endpoint_count,
      lines: (a, b) => b.line_count - a.line_count,
      name: (a, b) => a.short_name.localeCompare(b.short_name),
      modified: (a, b) => (b.last_modified || '').localeCompare(a.last_modified || ''),
    };
    return rows.sort(sorters[sortBy]);
  }, [data, query, sortBy]);

  const toggle = (name) => setExpanded((e) => ({ ...e, [name]: !e[name] }));

  const copyEndpoint = (ep) => {
    const text = `${ep.method} ${ep.path}`;
    navigator.clipboard?.writeText(text);
    setCopied(text);
    toast.success(`Copied: ${text}`);
    setTimeout(() => setCopied(null), 1200);
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const methodColor = (m) => ({
    GET: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    POST: 'bg-emerald-500/10 text-cyan-300 border-emerald-500/30',
    PUT: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    PATCH: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    DELETE: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  }[m] || 'bg-slate-500/10 text-slate-300 border-slate-500/30');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-slate-950 text-slate-100">
        <Card className="bg-rose-500/10 border-rose-500/30 max-w-lg">
          <CardContent className="p-6 text-rose-200">
            <h2 className="text-lg font-bold mb-2">Could not load router map</h2>
            <p className="text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { summary } = data;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="admin-router-map">
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/server-modules')}
              className="text-slate-400 hover:text-white mb-3 -ml-3"
              data-testid="router-map-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Admin
            </Button>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Route className="w-7 h-7 text-cyan-400" />
              Router Map
            </h1>
            <p className="text-sm text-slate-400 max-w-2xl">
              Every extracted router, its endpoints, file stats, and matching
              contract tests — read directly from{' '}
              <code className="px-1.5 py-0.5 rounded bg-slate-800 text-cyan-300 text-xs">
                services/router_registry.py
              </code>
              . Single source of truth.
            </p>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="router-map-summary">
          <SummaryTile label="Routers" value={summary.registry_size} icon={Route} tint="cyan" />
          <SummaryTile label="Endpoints" value={summary.total_endpoints} icon={FileCode2} tint="emerald" />
          <SummaryTile label="Lines in routes" value={summary.total_lines.toLocaleString()} icon={FileCode2} tint="violet" />
          <SummaryTile label="Contract tests" value={summary.tests_folder_count} icon={FlaskConical} tint="amber" />
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="router-map-search"
              placeholder="Search module, endpoint, or note…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 bg-slate-900 border-slate-800 text-white placeholder:text-slate-400"
            />
          </div>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-slate-400 mr-1">Sort:</span>
            {[
              { id: 'endpoints', label: 'Endpoints' },
              { id: 'lines', label: 'Lines' },
              { id: 'name', label: 'Name' },
              { id: 'modified', label: 'Modified' },
            ].map((s) => (
              <button
                key={s.id}
                data-testid={`router-map-sort-${s.id}`}
                onClick={() => setSortBy(s.id)}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  sortBy === s.id
                    ? 'bg-emerald-500/20 text-cyan-300 border border-emerald-500/30'
                    : 'text-slate-400 hover:text-white border border-transparent'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-slate-400 ml-auto">
            Showing <span className="text-slate-900 font-semibold">{filtered.length}</span> / {data.routers.length}
          </div>
        </div>

        {/* Router list */}
        <div className="space-y-2" data-testid="router-map-list">
          {filtered.map((r) => {
            const isOpen = !!expanded[r.short_name];
            return (
              <Card
                key={r.module_path}
                data-testid={`router-map-item-${r.short_name}`}
                className={`bg-slate-900 text-slate-100 border-slate-800 hover:border-slate-700 transition-colors ${
                  r.import_error ? 'border-rose-500/40' : ''
                }`}
              >
                <CardContent className="p-0">
                  <button
                    onClick={() => toggle(r.short_name)}
                    className="w-full p-4 flex items-center gap-4 text-left hover:bg-slate-900 transition-colors"
                    data-testid={`router-map-toggle-${r.short_name}`}
                  >
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-white truncate">
                          {r.short_name}
                        </h3>
                        {r.prefix && (
                          <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[11px] font-mono">
                            {r.prefix}
                          </Badge>
                        )}
                        {r.import_error && (
                          <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 text-[11px]">
                            <AlertTriangle className="w-3 h-3 mr-1 inline" />
                            import failed
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 font-mono truncate mt-0.5">
                        {r.file_path} · <span className="text-slate-400">{r.note}</span>
                      </p>
                    </div>

                    <div className="hidden sm:flex items-center gap-4 text-xs text-slate-400 flex-shrink-0">
                      <div className="flex items-center gap-1">
                        <FileCode2 className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="font-semibold text-white">{r.endpoint_count}</span>
                        <span className="text-slate-400">ep</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-white">{r.line_count.toLocaleString()}</span>
                        <span className="text-slate-400">LOC</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FlaskConical className={`w-3.5 h-3.5 ${r.matching_tests.length > 0 ? 'text-amber-400' : 'text-slate-400'}`} />
                        <span className="font-semibold text-white">{r.matching_tests.length}</span>
                        <span className="text-slate-400">tests</span>
                      </div>
                      <div className="flex items-center gap-1 min-w-[90px]">
                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                        <span>{fmtDate(r.last_modified)}</span>
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-800 p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Endpoints */}
                      <div>
                        <h4 className="text-xs font-bold tracking-widest text-slate-400 mb-2">
                          ENDPOINTS ({r.endpoints.length})
                        </h4>
                        {r.endpoints.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">No endpoints introspected.</p>
                        ) : (
                          <div className="space-y-1">
                            {r.endpoints.map((ep, i) => (
                              <div
                                key={i}
                                className="flex items-center gap-2 text-xs font-mono group"
                              >
                                <Badge className={`${methodColor(ep.method)} text-[11px] min-w-[48px] justify-center`}>
                                  {ep.method}
                                </Badge>
                                <span className="text-slate-400 flex-1 truncate">{ep.path}</span>
                                <button
                                  onClick={() => copyEndpoint(ep)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-cyan-400"
                                  data-testid={`router-map-copy-${r.short_name}-${i}`}
                                  aria-label={`Copy ${ep.method} ${ep.path}`}
                                >
                                  {copied === `${ep.method} ${ep.path}` ? (
                                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Tests + metadata */}
                      <div>
                        <h4 className="text-xs font-bold tracking-widest text-slate-400 mb-2">
                          CONTRACT TESTS ({r.matching_tests.length})
                        </h4>
                        {r.matching_tests.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">No matching test files found.</p>
                        ) : (
                          <div className="space-y-1 mb-4">
                            {r.matching_tests.map((t) => (
                              <div
                                key={t}
                                className="flex items-center gap-2 text-xs font-mono text-slate-400"
                              >
                                <FlaskConical className="w-3.5 h-3.5 text-amber-400" />
                                <span>{t}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <h4 className="text-xs font-bold tracking-widest text-slate-400 mb-2 mt-4">
                          MODULE
                        </h4>
                        <div className="text-xs font-mono text-slate-400 space-y-0.5">
                          <div>
                            <span className="text-slate-400">path: </span>
                            {r.module_path}
                          </div>
                          {r.import_error && (
                            <div className="text-rose-400 mt-2">
                              <span className="text-slate-400">error: </span>
                              {r.import_error}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400 text-sm">
              No routers match "{query}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryTile = ({ label, value, icon: Icon, tint }) => {
  const tints = {
    cyan: 'text-cyan-300 bg-emerald-500/10 border-emerald-500/20',
    emerald: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
    violet: 'text-cyan-300 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-300 bg-amber-500/10 border-amber-500/20',
  };
  return (
    <Card className="bg-slate-900 text-slate-100 border-slate-800">
      <CardContent className="p-4">
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center mb-3 ${tints[tint]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-2xl font-bold text-white leading-none mb-1">{value}</div>
        <div className="text-[11px] font-semibold tracking-wide text-slate-400 uppercase">
          {label}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminRouterMap;
